import type { UnitType } from './types';

/**
 * Combat effectiveness matrix — multiplier applied to attacker's effective attack
 * based on (attacker type, defender type) pair.
 *
 * Values:
 *   0.0       = attack impossible (canAttack returns false)
 *   0.05–0.2  = very ineffective (bullets vs armor → max(1) floor = 1 dmg)
 *   1.0       = baseline
 *   1.2–2.0   = effective/devastating (torpedoes vs ships, flak vs aircraft)
 *
 * Faction-unique units have their own rows/columns (no resolveBaseType).
 */

// Helper: row of all zeros for non-combat units (attack = 0)
function zeroRow(): Record<UnitType, number> {
  return {
    // Land: soft
    'infantry': 0, 'infantry-bazooka': 0, 'recon': 0, 'cavalry': 0,
    // Land: vehicles
    'jeep': 0, 'supply-truck': 0,
    // Land: armor
    'light-tank': 0, 'medium-tank': 0, 'heavy-tank': 0, 'apc': 0,
    'tank-destroyer': 0, 'aa-flakpanzer': 0,
    // Land: artillery
    'long-range-artillery': 0, 'anti-tank-artillery': 0,
    // Land: support
    'medic': 0,
    // Air
    'plane-fighter': 0, 'plane-bomber': 0, 'plane-transport': 0,
    // Water
    'ship-battleship': 0, 'ship-submarine': 0, 'ship-transport': 0, 'ship-aircraft-carrier': 0,
    // Faction: Polska
    'polska-ulani': 0, 'polska-7tp': 0,
    // Faction: ZSRR
    'zsrr-t34': 0, 'zsrr-katiusza': 0,
    // Faction: III Rzesza
    'iii-rzesza-tiger': 0, 'iii-rzesza-stuka': 0,
    // Faction: USA
    'usa-sherman': 0, 'usa-lcvp-higgins': 0,
    // Faction: Włochy
    'wlochy-l3-33': 0, 'wlochy-sm79-sparviero': 0,
    // Faction: Japonia
    'japonia-chi-ha': 0, 'japonia-a6m-zero': 0,
    // Faction: Francja
    'francja-renault-r35': 0, 'francja-ms406': 0,
    // Faction: Jugosławia
    'jugoslawia-partyzanci': 0, 'jugoslawia-ba64': 0,
    // Faction: Wielka Brytania
    'wielka-brytania-komandosi': 0, 'wielka-brytania-churchill': 0,
    // Commanders (officer + heroes) — defender columns filled below to match
    // each row's vs-infantry value; here they're placeholders for the type.
    'officer': 0,
    'hero-polska-a': 0, 'hero-polska-b': 0,
    'hero-zsrr-a': 0, 'hero-zsrr-b': 0,
    'hero-iii-rzesza-a': 0, 'hero-iii-rzesza-b': 0,
    'hero-usa-a': 0, 'hero-usa-b': 0,
    'hero-wlochy-a': 0, 'hero-wlochy-b': 0,
    'hero-japonia-a': 0, 'hero-japonia-b': 0,
    'hero-francja-a': 0, 'hero-francja-b': 0,
    'hero-jugoslawia-a': 0, 'hero-jugoslawia-b': 0,
    'hero-wielka-brytania-a': 0, 'hero-wielka-brytania-b': 0,
  };
}

/** All commander defender/attacker keys (officer + 18 heroes). */
const COMMANDER_TYPES: UnitType[] = [
  'officer',
  'hero-polska-a', 'hero-polska-b', 'hero-zsrr-a', 'hero-zsrr-b',
  'hero-iii-rzesza-a', 'hero-iii-rzesza-b', 'hero-usa-a', 'hero-usa-b',
  'hero-wlochy-a', 'hero-wlochy-b', 'hero-japonia-a', 'hero-japonia-b',
  'hero-francja-a', 'hero-francja-b', 'hero-jugoslawia-a', 'hero-jugoslawia-b',
  'hero-wielka-brytania-a', 'hero-wielka-brytania-b',
];

// ---------------------------------------------------------------------------
// Base type rows (22 types)
// ---------------------------------------------------------------------------

// infantry — karabiny, skuteczne vs miękkie cele, bezskuteczne vs pancerz/lotnictwo/marynarka
const infantry: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.0, 'infantry-bazooka': 1.0, 'recon': 1.0, 'cavalry': 1.0, 'medic': 1.0,
  // vs light vehicles
  'jeep': 0.8, 'supply-truck': 1.0,
  // vs armor
  'light-tank': 0.1, 'medium-tank': 0.1, 'heavy-tank': 0.05, 'apc': 0.1,
  'tank-destroyer': 0.1, 'aa-flakpanzer': 0.2,
  // vs artillery (soft targets)
  'long-range-artillery': 1.0, 'anti-tank-artillery': 1.0,
  // vs air: 0  vs water: 0
  // Faction soft targets — same as base
  'polska-ulani': 1.0, 'jugoslawia-partyzanci': 1.0, 'wielka-brytania-komandosi': 1.0, 'jugoslawia-ba64': 1.0,
  // Faction armor — same as base armor
  'polska-7tp': 0.1, 'zsrr-t34': 0.1, 'usa-sherman': 0.1,
  'wlochy-l3-33': 0.1, 'japonia-chi-ha': 0.1, 'francja-renault-r35': 0.1,
  'iii-rzesza-tiger': 0.05, 'wielka-brytania-churchill': 0.05,
  // Faction air — 0
  'wlochy-sm79-sparviero': 0, 'japonia-a6m-zero': 0, 'francja-ms406': 0, 'iii-rzesza-stuka': 0,
  // Faction water — 0
  'usa-lcvp-higgins': 0,
  // Faction artillery
  'zsrr-katiusza': 1.0,
};

// infantry-bazooka — bazooka, skuteczne vs pancerz, słabe vs piechota
const infantryBazooka: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft (bazooka not ideal vs people)
  'infantry': 0.5, 'infantry-bazooka': 0.5, 'recon': 0.5, 'cavalry': 0.5, 'medic': 0.5,
  // vs light vehicles
  'jeep': 1.0, 'supply-truck': 0.6,
  // vs armor — THIS IS WHAT BAZOOKA IS FOR
  'light-tank': 1.4, 'medium-tank': 1.4, 'heavy-tank': 1.2, 'apc': 1.2,
  'tank-destroyer': 1.3, 'aa-flakpanzer': 1.0,
  // vs artillery
  'long-range-artillery': 0.6, 'anti-tank-artillery': 0.6,
  // Faction soft
  'polska-ulani': 0.5, 'jugoslawia-partyzanci': 0.5, 'wielka-brytania-komandosi': 0.5, 'jugoslawia-ba64': 0.5,
  // Faction armor
  'polska-7tp': 1.4, 'zsrr-t34': 1.4, 'usa-sherman': 1.4,
  'wlochy-l3-33': 1.4, 'japonia-chi-ha': 1.4, 'francja-renault-r35': 1.4,
  'iii-rzesza-tiger': 1.2, 'wielka-brytania-churchill': 1.2,
  // Faction artillery
  'zsrr-katiusza': 0.6,
};

// recon — karabiny, same profile as infantry
const recon: Record<UnitType, number> = { ...infantry };

// cavalry — karabiny + szarża, bonus vs artyleria i miękkie cele
const cavalry: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft (charge bonus)
  'infantry': 1.1, 'infantry-bazooka': 1.1, 'recon': 1.1, 'cavalry': 1.0, 'medic': 1.1,
  // vs light vehicles
  'jeep': 0.7, 'supply-truck': 1.2,
  // vs armor
  'light-tank': 0.1, 'medium-tank': 0.05, 'heavy-tank': 0.05, 'apc': 0.1,
  'tank-destroyer': 0.1, 'aa-flakpanzer': 0.2,
  // vs artillery — SZARŻA KAWALERYJSKA!
  'long-range-artillery': 1.2, 'anti-tank-artillery': 1.2,
  // Faction soft
  'polska-ulani': 1.0, 'jugoslawia-partyzanci': 1.1, 'wielka-brytania-komandosi': 1.1, 'jugoslawia-ba64': 1.1,
  // Faction armor
  'polska-7tp': 0.1, 'zsrr-t34': 0.05, 'usa-sherman': 0.05,
  'wlochy-l3-33': 0.1, 'japonia-chi-ha': 0.05, 'francja-renault-r35': 0.1,
  'iii-rzesza-tiger': 0.05, 'wielka-brytania-churchill': 0.05,
  // Faction artillery
  'zsrr-katiusza': 1.2,
};

// jeep — zamontowany KM, similar to infantry but slightly better vs soft
const jeep: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.1, 'infantry-bazooka': 1.1, 'recon': 1.1, 'cavalry': 1.0, 'medic': 1.1,
  // vs light vehicles
  'jeep': 1.0, 'supply-truck': 1.0,
  // vs armor
  'light-tank': 0.1, 'medium-tank': 0.1, 'heavy-tank': 0.05, 'apc': 0.1,
  'tank-destroyer': 0.1, 'aa-flakpanzer': 0.2,
  // vs artillery
  'long-range-artillery': 1.0, 'anti-tank-artillery': 1.0,
  // Faction soft
  'polska-ulani': 1.0, 'jugoslawia-partyzanci': 1.1, 'wielka-brytania-komandosi': 1.1, 'jugoslawia-ba64': 1.1,
  // Faction armor
  'polska-7tp': 0.1, 'zsrr-t34': 0.1, 'usa-sherman': 0.1,
  'wlochy-l3-33': 0.1, 'japonia-chi-ha': 0.1, 'francja-renault-r35': 0.1,
  'iii-rzesza-tiger': 0.05, 'wielka-brytania-churchill': 0.05,
  // Faction artillery
  'zsrr-katiusza': 1.0,
};

// light-tank — lekkie działo czołgowe + KM
const lightTank: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.3, 'infantry-bazooka': 1.2, 'recon': 1.3, 'cavalry': 1.3, 'medic': 1.3,
  // vs light vehicles
  'jeep': 1.2, 'supply-truck': 1.3,
  // vs armor
  'light-tank': 0.9, 'medium-tank': 0.7, 'heavy-tank': 0.5, 'apc': 0.9,
  'tank-destroyer': 0.8, 'aa-flakpanzer': 1.0,
  // vs artillery
  'long-range-artillery': 1.3, 'anti-tank-artillery': 1.2,
  // Faction soft
  'polska-ulani': 1.3, 'jugoslawia-partyzanci': 1.3, 'wielka-brytania-komandosi': 1.3, 'jugoslawia-ba64': 1.3,
  // Faction armor
  'polska-7tp': 0.9, 'zsrr-t34': 0.7, 'usa-sherman': 0.7,
  'wlochy-l3-33': 0.9, 'japonia-chi-ha': 0.7, 'francja-renault-r35': 0.9,
  'iii-rzesza-tiger': 0.5, 'wielka-brytania-churchill': 0.5,
  // Faction artillery
  'zsrr-katiusza': 1.3,
};

// medium-tank — średnie działo czołgowe
const mediumTank: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.3, 'infantry-bazooka': 1.2, 'recon': 1.3, 'cavalry': 1.3, 'medic': 1.3,
  // vs light vehicles
  'jeep': 1.2, 'supply-truck': 1.3,
  // vs armor
  'light-tank': 1.1, 'medium-tank': 1.0, 'heavy-tank': 0.7, 'apc': 1.0,
  'tank-destroyer': 1.0, 'aa-flakpanzer': 1.1,
  // vs artillery
  'long-range-artillery': 1.3, 'anti-tank-artillery': 1.3,
  // Faction soft
  'polska-ulani': 1.3, 'jugoslawia-partyzanci': 1.3, 'wielka-brytania-komandosi': 1.3, 'jugoslawia-ba64': 1.3,
  // Faction armor
  'polska-7tp': 1.1, 'zsrr-t34': 1.0, 'usa-sherman': 1.0,
  'wlochy-l3-33': 1.1, 'japonia-chi-ha': 1.0, 'francja-renault-r35': 1.1,
  'iii-rzesza-tiger': 0.7, 'wielka-brytania-churchill': 0.7,
  // Faction artillery
  'zsrr-katiusza': 1.3,
};

// heavy-tank — ciężkie działo czołgowe
const heavyTank: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.3, 'infantry-bazooka': 1.2, 'recon': 1.3, 'cavalry': 1.3, 'medic': 1.3,
  // vs light vehicles
  'jeep': 1.3, 'supply-truck': 1.3,
  // vs armor
  'light-tank': 1.3, 'medium-tank': 1.1, 'heavy-tank': 1.0, 'apc': 1.2,
  'tank-destroyer': 1.1, 'aa-flakpanzer': 1.2,
  // vs artillery
  'long-range-artillery': 1.3, 'anti-tank-artillery': 1.3,
  // Faction soft
  'polska-ulani': 1.3, 'jugoslawia-partyzanci': 1.3, 'wielka-brytania-komandosi': 1.3, 'jugoslawia-ba64': 1.3,
  // Faction armor
  'polska-7tp': 1.3, 'zsrr-t34': 1.1, 'usa-sherman': 1.1,
  'wlochy-l3-33': 1.3, 'japonia-chi-ha': 1.1, 'francja-renault-r35': 1.3,
  'iii-rzesza-tiger': 1.0, 'wielka-brytania-churchill': 1.0,
  // Faction artillery
  'zsrr-katiusza': 1.3,
};

// tank-destroyer — działo AT, dewastujące vs pancerz, słabe vs piechota
const tankDestroyer: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 0.5, 'infantry-bazooka': 0.5, 'recon': 0.5, 'cavalry': 0.5, 'medic': 0.5,
  // vs light vehicles
  'jeep': 0.8, 'supply-truck': 0.6,
  // vs armor — SPECJALIZACJA AT
  'light-tank': 1.6, 'medium-tank': 1.8, 'heavy-tank': 1.6, 'apc': 1.4,
  'tank-destroyer': 1.2, 'aa-flakpanzer': 0.8,
  // vs artillery
  'long-range-artillery': 0.6, 'anti-tank-artillery': 0.6,
  // Faction soft
  'polska-ulani': 0.5, 'jugoslawia-partyzanci': 0.5, 'wielka-brytania-komandosi': 0.5, 'jugoslawia-ba64': 0.5,
  // Faction armor
  'polska-7tp': 1.6, 'zsrr-t34': 1.8, 'usa-sherman': 1.8,
  'wlochy-l3-33': 1.6, 'japonia-chi-ha': 1.8, 'francja-renault-r35': 1.6,
  'iii-rzesza-tiger': 1.6, 'wielka-brytania-churchill': 1.6,
  // Faction artillery
  'zsrr-katiusza': 0.6,
};

// aa-flakpanzer — ciężkie KM / Flak, dewastujące vs lotnictwo
const aaFlakpanzer: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 0.8, 'infantry-bazooka': 0.8, 'recon': 0.8, 'cavalry': 0.8, 'medic': 0.8,
  // vs light vehicles
  'jeep': 0.6, 'supply-truck': 0.8,
  // vs armor — KM nie przebije
  'light-tank': 0.2, 'medium-tank': 0.1, 'heavy-tank': 0.1, 'apc': 0.2,
  'tank-destroyer': 0.2, 'aa-flakpanzer': 0.3,
  // vs artillery
  'long-range-artillery': 0.8, 'anti-tank-artillery': 0.8,
  // vs air — DO TEGO JEST FLAK!
  'plane-fighter': 1.6, 'plane-bomber': 2.0, 'plane-transport': 2.0,
  // Faction soft
  'polska-ulani': 0.8, 'jugoslawia-partyzanci': 0.8, 'wielka-brytania-komandosi': 0.8, 'jugoslawia-ba64': 0.8,
  // Faction armor
  'polska-7tp': 0.2, 'zsrr-t34': 0.1, 'usa-sherman': 0.1,
  'wlochy-l3-33': 0.2, 'japonia-chi-ha': 0.1, 'francja-renault-r35': 0.2,
  'iii-rzesza-tiger': 0.1, 'wielka-brytania-churchill': 0.1,
  // Faction air
  'wlochy-sm79-sparviero': 1.6, 'japonia-a6m-zero': 1.6, 'francja-ms406': 1.6,
  'iii-rzesza-stuka': 2.0,
  // Faction artillery
  'zsrr-katiusza': 0.8,
};

// long-range-artillery — pociski HE, skuteczne vs piechota, słabe vs pancerz, bateria nabrzeżna vs okręty
const longRangeArtillery: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.2, 'infantry-bazooka': 1.2, 'recon': 1.2, 'cavalry': 1.2, 'medic': 1.2,
  // vs light vehicles
  'jeep': 1.0, 'supply-truck': 1.2,
  // vs armor
  'light-tank': 0.6, 'medium-tank': 0.5, 'heavy-tank': 0.4, 'apc': 0.6,
  'tank-destroyer': 0.6, 'aa-flakpanzer': 0.8,
  // vs artillery (kontrbateryjna)
  'long-range-artillery': 1.0, 'anti-tank-artillery': 1.0,
  // vs water (bateria nabrzeżna)
  'ship-battleship': 0.7, 'ship-transport': 0.7, 'ship-aircraft-carrier': 0.7,
  // vs submarine: 0 (nie widzi)
  // vs air: 0
  // Faction soft
  'polska-ulani': 1.2, 'jugoslawia-partyzanci': 1.2, 'wielka-brytania-komandosi': 1.2, 'jugoslawia-ba64': 1.2,
  // Faction armor
  'polska-7tp': 0.6, 'zsrr-t34': 0.5, 'usa-sherman': 0.5,
  'wlochy-l3-33': 0.6, 'japonia-chi-ha': 0.5, 'francja-renault-r35': 0.6,
  'iii-rzesza-tiger': 0.4, 'wielka-brytania-churchill': 0.4,
  // Faction water
  'usa-lcvp-higgins': 0.7,
  // Faction artillery
  'zsrr-katiusza': 1.0,
};

// anti-tank-artillery — pociski AP, same profile as tank-destroyer
const antiTankArtillery: Record<UnitType, number> = {
  ...tankDestroyer,
  // AT artillery can also hit ships with AP (weak but possible)
  'ship-battleship': 0.3, 'ship-transport': 0.3, 'ship-aircraft-carrier': 0.3,
  'usa-lcvp-higgins': 0.3,
};

// plane-fighter — KM lotnicze, dominacja powietrzna, słabe vs ląd
const planeFighter: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft (strafing)
  'infantry': 0.8, 'infantry-bazooka': 0.8, 'recon': 0.8, 'cavalry': 0.8, 'medic': 0.8,
  // vs light vehicles
  'jeep': 0.7, 'supply-truck': 0.8,
  // vs armor — KM nie przebiją
  'light-tank': 0.3, 'medium-tank': 0.2, 'heavy-tank': 0.1, 'apc': 0.3,
  'tank-destroyer': 0.3, 'aa-flakpanzer': 0.4,
  // vs artillery
  'long-range-artillery': 0.8, 'anti-tank-artillery': 0.8,
  // vs air — DOMINACJA
  'plane-fighter': 1.0, 'plane-bomber': 1.4, 'plane-transport': 1.4,
  // vs water
  'ship-battleship': 0.3, 'ship-transport': 0.4, 'ship-aircraft-carrier': 0.4,
  // vs submarine: 0
  // Faction soft
  'polska-ulani': 0.8, 'jugoslawia-partyzanci': 0.8, 'wielka-brytania-komandosi': 0.8, 'jugoslawia-ba64': 0.8,
  // Faction armor
  'polska-7tp': 0.3, 'zsrr-t34': 0.2, 'usa-sherman': 0.2,
  'wlochy-l3-33': 0.3, 'japonia-chi-ha': 0.2, 'francja-renault-r35': 0.3,
  'iii-rzesza-tiger': 0.1, 'wielka-brytania-churchill': 0.1,
  // Faction air
  'wlochy-sm79-sparviero': 1.0, 'japonia-a6m-zero': 1.0, 'francja-ms406': 1.0,
  'iii-rzesza-stuka': 1.4,
  // Faction water
  'usa-lcvp-higgins': 0.4,
  // Faction artillery
  'zsrr-katiusza': 0.8,
};

// plane-bomber — bomby lotnicze, skuteczne vs ląd i okręty, słabe w dogfight
const planeBomber: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft
  'infantry': 1.3, 'infantry-bazooka': 1.3, 'recon': 1.3, 'cavalry': 1.3, 'medic': 1.3,
  // vs light vehicles
  'jeep': 1.2, 'supply-truck': 1.3,
  // vs armor
  'light-tank': 1.0, 'medium-tank': 0.9, 'heavy-tank': 0.8, 'apc': 1.0,
  'tank-destroyer': 1.0, 'aa-flakpanzer': 1.0,
  // vs artillery
  'long-range-artillery': 1.3, 'anti-tank-artillery': 1.3,
  // vs air — bombowiec nie umie walczyć
  'plane-fighter': 0.3, 'plane-bomber': 0.5, 'plane-transport': 0.5,
  // vs water
  'ship-battleship': 1.2, 'ship-submarine': 0.3, 'ship-transport': 1.2, 'ship-aircraft-carrier': 1.2,
  // Faction soft
  'polska-ulani': 1.3, 'jugoslawia-partyzanci': 1.3, 'wielka-brytania-komandosi': 1.3, 'jugoslawia-ba64': 1.3,
  // Faction armor
  'polska-7tp': 1.0, 'zsrr-t34': 0.9, 'usa-sherman': 0.9,
  'wlochy-l3-33': 1.0, 'japonia-chi-ha': 0.9, 'francja-renault-r35': 1.0,
  'iii-rzesza-tiger': 0.8, 'wielka-brytania-churchill': 0.8,
  // Faction air
  'wlochy-sm79-sparviero': 0.3, 'japonia-a6m-zero': 0.3, 'francja-ms406': 0.3,
  'iii-rzesza-stuka': 0.5,
  // Faction water
  'usa-lcvp-higgins': 1.2,
  // Faction artillery
  'zsrr-katiusza': 1.3,
};

// ship-battleship — działa okrętowe, ostrzał nabrzeżny, słabe vs lotnictwo
const shipBattleship: Record<UnitType, number> = {
  ...zeroRow(),
  // vs soft (shore bombardment)
  'infantry': 1.3, 'infantry-bazooka': 1.3, 'recon': 1.3, 'cavalry': 1.3, 'medic': 1.3,
  // vs light vehicles
  'jeep': 1.2, 'supply-truck': 1.3,
  // vs armor
  'light-tank': 1.0, 'medium-tank': 1.0, 'heavy-tank': 0.8, 'apc': 1.0,
  'tank-destroyer': 1.0, 'aa-flakpanzer': 1.0,
  // vs artillery
  'long-range-artillery': 1.3, 'anti-tank-artillery': 1.3,
  // vs air (słabe AA)
  'plane-fighter': 0.3, 'plane-bomber': 0.3, 'plane-transport': 0.3,
  // vs water
  'ship-battleship': 1.0, 'ship-submarine': 0.5, 'ship-transport': 1.2, 'ship-aircraft-carrier': 1.0,
  // Faction soft
  'polska-ulani': 1.3, 'jugoslawia-partyzanci': 1.3, 'wielka-brytania-komandosi': 1.3, 'jugoslawia-ba64': 1.3,
  // Faction armor
  'polska-7tp': 1.0, 'zsrr-t34': 1.0, 'usa-sherman': 1.0,
  'wlochy-l3-33': 1.0, 'japonia-chi-ha': 1.0, 'francja-renault-r35': 1.0,
  'iii-rzesza-tiger': 0.8, 'wielka-brytania-churchill': 0.8,
  // Faction air
  'wlochy-sm79-sparviero': 0.3, 'japonia-a6m-zero': 0.3, 'francja-ms406': 0.3,
  'iii-rzesza-stuka': 0.3,
  // Faction water
  'usa-lcvp-higgins': 1.2,
  // Faction artillery
  'zsrr-katiusza': 1.3,
};

// ship-submarine — torpedy, TYLKO vs cele morskie
const shipSubmarine: Record<UnitType, number> = {
  ...zeroRow(),
  // vs water — TORPEDY!
  'ship-battleship': 1.8, 'ship-submarine': 1.0, 'ship-transport': 1.8, 'ship-aircraft-carrier': 1.8,
  // Faction water
  'usa-lcvp-higgins': 1.8,
};

// ---------------------------------------------------------------------------
// Faction-unique rows — start from base type, apply tweaks
// ---------------------------------------------------------------------------

// polska-ulani (cavalry) — lepszy kawalerzysta, szarża na artyleryję wzmocniona
const polskaUlani: Record<UnitType, number> = {
  ...cavalry,
  // Ulani charge bonus vs artillery
  'long-range-artillery': 1.4, 'anti-tank-artillery': 1.4, 'zsrr-katiusza': 1.4,
  // Slightly better vs soft targets
  'infantry': 1.2, 'infantry-bazooka': 1.2, 'recon': 1.2, 'medic': 1.2,
  'polska-ulani': 1.0,
  'jugoslawia-partyzanci': 1.2, 'wielka-brytania-komandosi': 1.2,
};

// polska-7tp (light-tank) — lepszy lekki czołg
const polska7tp: Record<UnitType, number> = {
  ...lightTank,
  // Slightly better vs armor
  'light-tank': 1.0, 'medium-tank': 0.8, 'apc': 1.0,
  'polska-7tp': 1.0, 'wlochy-l3-33': 1.0, 'francja-renault-r35': 1.0,
};

// zsrr-t34 (medium-tank) — lepszy czołg średni, pochylony pancerz
const zsrrT34: Record<UnitType, number> = {
  ...mediumTank,
  // Better vs light tanks (sloped armor advantage)
  'light-tank': 1.2, 'polska-7tp': 1.2, 'wlochy-l3-33': 1.2, 'francja-renault-r35': 1.2,
  // Slightly better vs medium tanks
  'medium-tank': 1.1, 'zsrr-t34': 1.0, 'usa-sherman': 1.1, 'japonia-chi-ha': 1.1,
};

// zsrr-katiusza (long-range-artillery) — salwa rakiet, lepsze vs piechota
const zsrrKatiusza: Record<UnitType, number> = {
  ...longRangeArtillery,
  // Rocket salvo devastating vs infantry concentrations
  'infantry': 1.4, 'infantry-bazooka': 1.4, 'recon': 1.4, 'cavalry': 1.4, 'medic': 1.4,
  'polska-ulani': 1.4, 'jugoslawia-partyzanci': 1.4, 'wielka-brytania-komandosi': 1.4, 'jugoslawia-ba64': 1.4,
};

// iii-rzesza-tiger (heavy-tank) — lepsze działo KwK 36
const iiiRzeszaTiger: Record<UnitType, number> = {
  ...heavyTank,
  // Better vs heavy armor (KwK 36 L/56)
  'heavy-tank': 1.1, 'iii-rzesza-tiger': 1.0, 'wielka-brytania-churchill': 1.1,
  // Better vs medium tanks
  'medium-tank': 1.2, 'zsrr-t34': 1.2, 'usa-sherman': 1.2, 'japonia-chi-ha': 1.2,
};

// iii-rzesza-stuka (plane-bomber) — precyzyjne nurkowanie, lepsze vs pancerz
const iiiRzeszaStuka: Record<UnitType, number> = {
  ...planeBomber,
  // Precision dive bombing — better vs armor
  'light-tank': 1.1, 'medium-tank': 1.0, 'heavy-tank': 0.9, 'apc': 1.1,
  'tank-destroyer': 1.1, 'aa-flakpanzer': 1.1,
  'polska-7tp': 1.1, 'zsrr-t34': 1.0, 'usa-sherman': 1.0,
  'wlochy-l3-33': 1.1, 'japonia-chi-ha': 1.0, 'francja-renault-r35': 1.1,
  'iii-rzesza-tiger': 0.9, 'wielka-brytania-churchill': 0.9,
};

// usa-sherman (medium-tank) — lepszy czołg średni
const usaSherman: Record<UnitType, number> = {
  ...mediumTank,
  'light-tank': 1.2, 'polska-7tp': 1.2, 'wlochy-l3-33': 1.2, 'francja-renault-r35': 1.2,
  'medium-tank': 1.1, 'usa-sherman': 1.0, 'zsrr-t34': 1.1, 'japonia-chi-ha': 1.1,
};

// wlochy-l3-33 (light-tank) — lepszy lekki czołg
const wlochyL333: Record<UnitType, number> = {
  ...lightTank,
  'light-tank': 1.0, 'wlochy-l3-33': 1.0, 'polska-7tp': 1.0, 'francja-renault-r35': 1.0,
  'medium-tank': 0.8,
};

// wlochy-sm79-sparviero (plane-fighter) — lepszy myśliwiec
const wlochySm79: Record<UnitType, number> = {
  ...planeFighter,
  'plane-fighter': 1.1, 'wlochy-sm79-sparviero': 1.0,
  'plane-bomber': 1.5, 'plane-transport': 1.5,
  'iii-rzesza-stuka': 1.5,
};

// japonia-chi-ha (medium-tank) — lepszy czołg średni
const japoniaChibaHa: Record<UnitType, number> = {
  ...mediumTank,
  'light-tank': 1.2, 'polska-7tp': 1.2, 'wlochy-l3-33': 1.2, 'francja-renault-r35': 1.2,
  'medium-tank': 1.1, 'japonia-chi-ha': 1.0, 'zsrr-t34': 1.1, 'usa-sherman': 1.1,
};

// japonia-a6m-zero (plane-fighter) — zwrotność w dogfight
const japoniaA6mZero: Record<UnitType, number> = {
  ...planeFighter,
  'plane-fighter': 1.1, 'japonia-a6m-zero': 1.0,
  'plane-bomber': 1.5, 'plane-transport': 1.5,
  'iii-rzesza-stuka': 1.5,
};

// francja-renault-r35 (light-tank) — lepszy lekki czołg
const francjaRenaultR35: Record<UnitType, number> = {
  ...lightTank,
  'light-tank': 1.0, 'francja-renault-r35': 1.0, 'polska-7tp': 1.0, 'wlochy-l3-33': 1.0,
  'medium-tank': 0.8,
};

// francja-ms406 (plane-fighter) — lepszy myśliwiec
const francjaMs406: Record<UnitType, number> = {
  ...planeFighter,
  'plane-fighter': 1.1, 'francja-ms406': 1.0,
  'plane-bomber': 1.5, 'plane-transport': 1.5,
  'iii-rzesza-stuka': 1.5,
};

// jugoslawia-partyzanci (infantry) — rajdy partyzanckie
const jugoslawiaPartyzanci: Record<UnitType, number> = {
  ...infantry,
  // Guerrilla raids on artillery positions
  'long-range-artillery': 1.3, 'anti-tank-artillery': 1.3, 'zsrr-katiusza': 1.3,
  // Better vs soft targets
  'infantry': 1.1, 'infantry-bazooka': 1.1, 'recon': 1.1, 'cavalry': 1.1, 'medic': 1.1,
  'jugoslawia-partyzanci': 1.0,
  'polska-ulani': 1.1, 'wielka-brytania-komandosi': 1.1,
  // Better vs supply lines
  'supply-truck': 1.2,
};

// jugoslawia-ba64 (recon) — lepszy zwiad
const jugoslawiaBA64: Record<UnitType, number> = {
  ...recon,
  // Slightly better vs soft targets (armored car)
  'infantry': 1.1, 'infantry-bazooka': 1.1, 'recon': 1.1, 'cavalry': 1.1, 'medic': 1.1,
  'jugoslawia-ba64': 1.0,
  'jugoslawia-partyzanci': 1.1, 'polska-ulani': 1.1, 'wielka-brytania-komandosi': 1.1,
};

// wielka-brytania-komandosi (infantry) — elitarne wyszkolenie
const wielkaBrytaniaKomandosi: Record<UnitType, number> = {
  ...infantry,
  // Elite training — better vs all soft targets
  'infantry': 1.2, 'infantry-bazooka': 1.2, 'recon': 1.2, 'cavalry': 1.2, 'medic': 1.2,
  'wielka-brytania-komandosi': 1.0,
  'polska-ulani': 1.2, 'jugoslawia-partyzanci': 1.2, 'jugoslawia-ba64': 1.2,
  // Better vs light vehicles
  'jeep': 1.0, 'supply-truck': 1.2,
  // Slightly better vs armor (commando tactics)
  'light-tank': 0.15, 'medium-tank': 0.15, 'heavy-tank': 0.1,
  'polska-7tp': 0.15, 'zsrr-t34': 0.15, 'usa-sherman': 0.15,
  'wlochy-l3-33': 0.15, 'japonia-chi-ha': 0.15, 'francja-renault-r35': 0.15,
};

// wielka-brytania-churchill (heavy-tank) — słabsze działo, ale grubszy pancerz (wyrażony w statach def/HP)
const wielkaBrytaniaChurchill: Record<UnitType, number> = {
  ...heavyTank,
  // Weaker gun vs heavy targets
  'heavy-tank': 0.9, 'iii-rzesza-tiger': 0.9, 'wielka-brytania-churchill': 0.9,
  // Slightly weaker vs medium too
  'medium-tank': 1.0, 'zsrr-t34': 1.0, 'usa-sherman': 1.0, 'japonia-chi-ha': 1.0,
};

// ---------------------------------------------------------------------------
// Full matrix assembly
// ---------------------------------------------------------------------------

export const COMBAT_EFFECTIVENESS: Record<UnitType, Record<UnitType, number>> = {
  // Base types
  'infantry': infantry,
  'infantry-bazooka': infantryBazooka,
  'recon': recon,
  'cavalry': cavalry,
  'jeep': jeep,
  'light-tank': lightTank,
  'medium-tank': mediumTank,
  'heavy-tank': heavyTank,
  'apc': zeroRow(),
  'tank-destroyer': tankDestroyer,
  'aa-flakpanzer': aaFlakpanzer,
  'long-range-artillery': longRangeArtillery,
  'anti-tank-artillery': antiTankArtillery,
  'supply-truck': zeroRow(),
  'medic': zeroRow(),
  'plane-fighter': planeFighter,
  'plane-bomber': planeBomber,
  'plane-transport': zeroRow(),
  'ship-battleship': shipBattleship,
  'ship-submarine': shipSubmarine,
  'ship-transport': zeroRow(),
  'ship-aircraft-carrier': zeroRow(),
  // Faction: Polska
  'polska-ulani': polskaUlani,
  'polska-7tp': polska7tp,
  // Faction: ZSRR
  'zsrr-t34': zsrrT34,
  'zsrr-katiusza': zsrrKatiusza,
  // Faction: III Rzesza
  'iii-rzesza-tiger': iiiRzeszaTiger,
  'iii-rzesza-stuka': iiiRzeszaStuka,
  // Faction: USA
  'usa-sherman': usaSherman,
  'usa-lcvp-higgins': zeroRow(),
  // Faction: Włochy
  'wlochy-l3-33': wlochyL333,
  'wlochy-sm79-sparviero': wlochySm79,
  // Faction: Japonia
  'japonia-chi-ha': japoniaChibaHa,
  'japonia-a6m-zero': japoniaA6mZero,
  // Faction: Francja
  'francja-renault-r35': francjaRenaultR35,
  'francja-ms406': francjaMs406,
  // Faction: Jugosławia
  'jugoslawia-partyzanci': jugoslawiaPartyzanci,
  'jugoslawia-ba64': jugoslawiaBA64,
  // Faction: Wielka Brytania
  'wielka-brytania-komandosi': wielkaBrytaniaKomandosi,
  'wielka-brytania-churchill': wielkaBrytaniaChurchill,
  // Commanders attack like soft infantry (modest melee).
  'officer': { ...infantry },
  'hero-polska-a': { ...infantry }, 'hero-polska-b': { ...infantry },
  'hero-zsrr-a': { ...infantry }, 'hero-zsrr-b': { ...infantry },
  'hero-iii-rzesza-a': { ...infantry }, 'hero-iii-rzesza-b': { ...infantry },
  'hero-usa-a': { ...infantry }, 'hero-usa-b': { ...infantry },
  'hero-wlochy-a': { ...infantry }, 'hero-wlochy-b': { ...infantry },
  'hero-japonia-a': { ...infantry }, 'hero-japonia-b': { ...infantry },
  'hero-francja-a': { ...infantry }, 'hero-francja-b': { ...infantry },
  'hero-jugoslawia-a': { ...infantry }, 'hero-jugoslawia-b': { ...infantry },
  'hero-wielka-brytania-a': { ...infantry }, 'hero-wielka-brytania-b': { ...infantry },
};

// Commanders are soft land targets: every attacker's effectiveness vs a commander
// mirrors its effectiveness vs infantry. Fill those defender columns now.
for (const attacker of Object.keys(COMBAT_EFFECTIVENESS) as UnitType[]) {
  const row = COMBAT_EFFECTIVENESS[attacker];
  const vsInfantry = row['infantry'];
  for (const cmd of COMMANDER_TYPES) row[cmd] = vsInfantry;
}

/** Get combat effectiveness multiplier for attacker vs defender. */
export function getEffectiveness(attackerType: UnitType, defenderType: UnitType): number {
  return COMBAT_EFFECTIVENESS[attackerType][defenderType];
}
